import React, { useEffect, useState } from "react";
import {
  Modal,
  Table,
  Button,
  Tag,
  Space,
  Typography,
  message,
  Tooltip,
} from "antd";
import {
  DownloadOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { actions, reducer, sliceKey } from "./slice";
import { reportJobsSaga } from "./saga";
import {
  selectReportJobs,
  selectReportJobsLoading,
  selectReportJobsError,
  selectReportJobsPagination,
} from "./selectors";
import moment from "moment";

const { Text } = Typography;

const ReportJobsModal = ({ visible, onClose }) => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: reportJobsSaga });

  const jobs = useSelector(selectReportJobs);
  const loading = useSelector(selectReportJobsLoading);
  const error = useSelector(selectReportJobsError);
  const pagination = useSelector(selectReportJobsPagination);

  useEffect(() => {
    if (visible) {
      fetchJobs();
    }
  }, [visible, currentPage, pageSize]);

  const fetchJobs = () => {
    dispatch(
      actions.fetchReportJobs({
        page: currentPage,
        limit: pageSize,
      })
    );
  };

  const handleRefresh = () => {
    fetchJobs();
    message.success("Jobs list refreshed");
  };

  const handleDownload = (record) => {
    if (record.result?.downloadUrl) {
      window.open(record.result.downloadUrl, "_blank");
      message.success("Download started");
    } else {
      message.error("Download URL not available");
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      completed: { color: "success", icon: <CheckCircleOutlined /> },
      processing: { color: "processing", icon: <ClockCircleOutlined /> },
      pending: { color: "default", icon: <ClockCircleOutlined /> },
      failed: { color: "error", icon: <CloseCircleOutlined /> },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Tag color={config.color} icon={config.icon}>
        {status.toUpperCase()}
      </Tag>
    );
  };

  const getReportTypeLabel = (reportType) => {
    const typeLabels = {
      orderDetails: "Order Details",
      sampleDetails: "Sample Details",
      boxReport: "Box Report",
      correctionReport: "Correction Report",
      complianceReport: "Order-Wise Key Compliance Report",
      complianceMonitoringData: "Compliance Monitoring Data",
    };
    return typeLabels[reportType] || reportType;
  };

  const columns = [
    {
      title: "Report Type",
      dataIndex: "reportType",
      key: "reportType",
      render: (reportType) => (
        <Space>
          <FileTextOutlined />
          <Text strong>{getReportTypeLabel(reportType)}</Text>
        </Space>
      ),
      width: 250,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusTag(status),
      width: 120,
    },
    {
      title: "Total Records",
      dataIndex: "result",
      key: "result",
      render: (result) => (
        <Text>
          {result?.totalRecords || 0}
        </Text>
      ),
      width: 150,
    },
    {
      title: "File Info",
      dataIndex: "result",
      key: "fileInfo",
      render: (result) =>
        result ? (
          <Space direction="vertical" size={0}>
            <Text ellipsis style={{ maxWidth: 200 }}>
              {result.fileName}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {result.fileSizeFormatted} • {result.totalRecords} records
            </Text>
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        ),
      width: 220,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => (
        <Tooltip title={moment(createdAt).format("YYYY-MM-DD HH:mm:ss")}>
          <Text>{moment(createdAt).fromNow()}</Text>
        </Tooltip>
      ),
      width: 120,
    },
    {
      title: "Completed At",
      dataIndex: "completedAt",
      key: "completedAt",
      render: (completedAt) =>
        completedAt ? (
          <Tooltip title={moment(completedAt).format("YYYY-MM-DD HH:mm:ss")}>
            <Text>{moment(completedAt).fromNow()}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        ),
      width: 130,
    },
    {
      title: "Expires At",
      dataIndex: "expiresAt",
      key: "expiresAt",
      render: (expiresAt) => (
        <Tooltip title={moment(expiresAt).format("YYYY-MM-DD HH:mm:ss")}>
          <Text type={moment(expiresAt).isBefore(moment()) ? "danger" : ""}>
            {moment(expiresAt).fromNow()}
          </Text>
        </Tooltip>
      ),
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            size="small"
            disabled={record.status !== "completed" || !record.result?.downloadUrl}
            onClick={() => handleDownload(record)}
          >
            Download
          </Button>
        </Space>
      ),
      width: 130,
    },
  ];

  const handleTableChange = (paginationConfig) => {
    setCurrentPage(paginationConfig.current);
    setPageSize(paginationConfig.pageSize);
  };

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <span>Report Jobs</span>
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="refresh" icon={<ReloadOutlined />} onClick={handleRefresh}>
          Refresh
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={1200}
      style={{ top: 20 }}
      bodyStyle={{ padding: "16px 24px" }}
    >
      {error && (
        <div
          style={{
            backgroundColor: "#fff2e8",
            border: "1px solid #ffbb96",
            borderRadius: 4,
            padding: 12,
            marginBottom: 16,
          }}
        >
          <Text type="danger">{error}</Text>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={jobs}
        loading={loading}
        rowKey="_id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: pagination?.total || 0,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} jobs`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1400 }}
        size="small"
      />
    </Modal>
  );
};

export default ReportJobsModal;